<template>
  <div class="requirement-list">
    <p v-if="!requirements.length" class="text-muted small mb-0">{{ emptyText }}</p>

    <div v-for="row in requirements" :key="'req' + row.id" class="requirement-row" :class="'requirement-' + row.Status">
      <div class="requirement-main">
        <div class="requirement-title">
          <feather-icon :icon="statusIcon(row.Status)" size="16" :class="'text-' + variant(row.Status)" class="mr-50" />
          <strong>{{ row.Name }}</strong>
          <b-badge v-if="row.Required" variant="light-secondary" class="ml-50">Required</b-badge>
          <b-badge :variant="'light-' + variant(row.Status)" class="ml-50">{{ statusLabel(row.Status) }}</b-badge>
        </div>

        <small v-if="row.Note" class="requirement-note">
          <feather-icon icon="InfoIcon" size="12" class="mr-25" />
          {{ row.Note }}
        </small>

        <div v-if="row.Status === 'Rejected' && rejectReason(row)" class="requirement-reject">
          <feather-icon icon="AlertCircleIcon" size="14" class="mr-25" />
          <span><strong>Rejected:</strong> {{ rejectReason(row) }} — please upload a new file.</span>
        </div>

        <div v-if="row.CurrentDocument" class="requirement-file">
          <feather-icon icon="PaperclipIcon" size="12" class="mr-25" />
          <a href="#" @click.prevent="download(row.CurrentDocument)">{{ row.CurrentDocument.FileName }}</a>
          <span class="text-muted">
            · {{ formatSize(row.CurrentDocument.FileSize) }}
            <template v-if="row.CurrentDocument.created_at">· {{ formatDate(row.CurrentDocument.created_at) }}</template>
          </span>
          <small v-if="row.CurrentDocument.Note" class="text-muted d-block">Your note: {{ row.CurrentDocument.Note }}</small>
        </div>
      </div>

      <div class="requirement-actions">
        <b-button
          v-if="row.Status !== 'Approved'"
          variant="outline-primary"
          size="sm"
          :disabled="!canUpload"
          :title="!canUpload ? disabledHint : ''"
          @click="openUpload(row)"
        >
          <feather-icon icon="UploadIcon" size="14" class="mr-25" />
          {{ row.CurrentDocument ? "Replace" : "Upload" }}
        </b-button>
      </div>
    </div>

    <small v-if="!canUpload && requirements.length" class="text-muted d-block mt-25">{{ disabledHint }}</small>

    <upload-note-modal ref="uploadModal" @uploaded="$emit('payload', $event)" />
  </div>
</template>

<script>
// Rows of requirements (checklist) with status, SFC note, current file and an
// Upload / Replace button. Emits `payload` with the fresh page payload after an upload.
import UploadNoteModal from "./UploadNoteModal.vue";
import { downloadDocument, formatSize, formatDate, requirementStatusVariant, statusLabel } from "./publicApi";

export default {
  name: "RequirementUploadList",

  components: { UploadNoteModal },

  props: {
    requirements: { type: Array, default: () => [] },
    canUpload: { type: Boolean, default: true },
    disabledHint: { type: String, default: "Save your progress first to enable uploads." },
    emptyText: { type: String, default: "No documents have been requested." },
  },

  methods: {
    formatSize,
    formatDate,
    statusLabel,

    variant(status) {
      return requirementStatusVariant[status] || "secondary";
    },

    statusIcon(status) {
      return (
        {
          Requested: "CircleIcon",
          Submitted: "ClockIcon",
          UnderReview: "EyeIcon",
          Approved: "CheckCircleIcon",
          Rejected: "XCircleIcon",
        }[status] || "CircleIcon"
      );
    },

    rejectReason(row) {
      return row.RejectReason || (row.CurrentDocument && row.CurrentDocument.ReviewReason) || "";
    },

    openUpload(row) {
      this.$refs.uploadModal.open(row);
    },

    download(doc) {
      downloadDocument(doc.id, doc.FileName).catch(() => {
        this.$bvToast.toast("Download failed.", { title: "Error", variant: "danger", solid: true });
      });
    },
  },
};
</script>
